/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */

import React from 'react'
import { PrismicPreviewProvider } from 'gatsby-plugin-prismic-previews'
import { repositoryConfigs } from './src/utils/prismicPreviews'
import { DevDocRoot } from './src/utils/prismicDevRoot'
import { FallSaleProvider } from './src/js/feature-flags/use-fall-sale-promotion'
import $ from 'jquery'
import * as Sentry from '@sentry/browser';
import { updateDocument } from './src/js/functions';
import { languageSwitcher } from './src/js/language-switcher'

import { RudderStack } from './src/js/rudderstack';
import { trackMarketingRoute } from './src/js/analytics/marketing-events';

import { cloudProofing } from './src/js/cloud-proofing'
import { SmartAlbumsOverview } from './src/js/sa-overview'
import { SmartSlidesOverview } from './src/js/pss-overview'
import { ajaxFormsUpdateDocument } from './src/js/ajax_forms'
import { smartalbumsVideoTutorials } from './src/js/tutorials-smartalbums'
import { initSpoiler } from './src/js/spoiler'
import { checkout } from './src/js/checkout'

import { about } from './src/js/about'
import { smartalbumsTrialDownloadSignupFollow } from './src/js/smartalbums-trial-download-signup-follow'
import { blog } from './src/js/blog'
import { post } from './src/js/post'
import { smartalbumsDownload } from './src/js/smartalbums-download'
import { sammpsFeatures } from './src/js/sammps-features'
import { sammpsFeatures2 } from './src/js/sammps-features-2'
import { smartalbums2022 } from './src/js/smartalbums2022'
import { testimonials } from './src/js/testimonials'
import { resetGlobalBindings, waitForElement } from './src/js/_helpers'
import { setSentryCookie } from './src/js/set-sentry-cookie'
import { GalleriesOverview } from './src/js/psf-overview'
import { setUTMCookie } from './src/js/set-utm-cookie'
import { getOrCreatePxAnonymousId } from './src/js/analytics/anonymous-id'
import { scrollSlideDown } from './src/js/scroll-slide-down'
import { HomePage } from './src/js/homepage'
import { LandingPage } from './src/js/landing'
import { cssImagesLazyLoader } from './src/js/_css-images-lazy-loader';
import { txLanguages } from "./src/helpers/translations-helper";
import { AffiliatesPage } from './src/js/affiliates';
import { CalculatorPage } from './src/js/calculator';
import { LeadMagnet } from './src/js/lead-magnet';
import { featureFlags } from './src/js/feature-flags';
import { preloadFallSaleFlags } from './src/js/feature-flags/fall-sale-bootstrap';

export const onClientEntry = () => {
  // Remote config and flags start when a public client key is present (ADR-0009),
  // independent of whether any browser experiment is declared.
  // Preload fall-sale flags at app init (no Segment wait) so the banner can show when ready.
  featureFlags.initialize();
  preloadFallSaleFlags();
};

export const wrapRootElement = ({ element }) => (
  <PrismicPreviewProvider repositoryConfigs={repositoryConfigs}>
    <DevDocRoot>
      <FallSaleProvider>
        <React.Suspense fallback={<p>Loading preview…</p>}>
          {element}
        </React.Suspense>
      </FallSaleProvider>
    </DevDocRoot>
  </PrismicPreviewProvider>
)

// updateDocument is the equivalent of jQuery's document.ready
export const onRouteUpdate = ({
  location
}) => {
  // Hreflangs and canonical
  const languages = window.__LANGUAGES__ || [];

  const existingHreflangTags = document.querySelectorAll('link[rel="alternate"][hreflang]');
  existingHreflangTags.forEach((tag) => tag.remove());

  const existingCanonicalTags = document.querySelectorAll('link[rel="canonical"][href]');
  existingCanonicalTags.forEach((tag) => tag.remove());

  const pathnameArray = location.pathname.split('/');
  const langSubfolder = pathnameArray[1];
  let clearedPathname = (txLanguages.includes(langSubfolder)) ? location.pathname.slice(langSubfolder.length + 1, location.pathname.length) : location.pathname;

  if (!clearedPathname.includes('/blog') && !clearedPathname.includes('/videos') && !clearedPathname.includes('/sitemap')) {
    languages.forEach((lang) => {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.href = `${lang.url}${clearedPathname}`;
      link.hreflang = lang.code;
      document.head.appendChild(link);
    });
  }

  const canonicalLink = document.createElement("link");
  canonicalLink.rel = "canonical";
  canonicalLink.href = `${languages[0].url}${location.pathname}`;
  document.head.appendChild(canonicalLink);

  // Pages scripts
  function pagesScripts() {
    $('html, body').scrollTop(0);

    // Read the cookie
    function getCookie(name) {
      var matches = document.cookie.match(new RegExp(
        // eslint-disable-next-line
        "(?:^|; )" + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + "=([^;]*)"
      ));
      return matches ? decodeURIComponent(matches[1]) : undefined;
    }

    // Init the Sentry client if we have specific cookie
    if (getCookie('pws-run-sentry')) {
      Sentry.init({ dsn: window.px_config.Sentry_DSN });
    }

    setUTMCookie();
    try {
      getOrCreatePxAnonymousId();
    } catch {
      // Cookie access can throw; page scripts must continue without the join key.
    }

    // CSS images lazy loader
    cssImagesLazyLoader();

    languageSwitcher();

    resetGlobalBindings();

    updateDocument();
    ajaxFormsUpdateDocument();

    const currentLocation = location.pathname.replace(/\/$/, '');

    if (currentLocation.includes('/pricing')) {
      let firstLoad = true;

      if (firstLoad === true) {
        if (
          currentLocation.endsWith('/pricing/smartgalleries') ||
          currentLocation.endsWith('/pricing/smartgalleries/')
        ) {
          testimonials();
        }

        initSpoiler();

        waitForElement('px-rpt').then((elm) => {
          checkout();
        });

        firstLoad = false;
      }
    } else if (
      currentLocation.endsWith('/smartalbums/cloud-proofing') ||
      currentLocation.endsWith('/smartalbums/cloud-proofing/')
    ) {
      cloudProofing();
    } else if (
      location.pathname.endsWith('/smartalbums')
      || location.pathname.endsWith('/smartalbums/')
    ) {
      SmartAlbumsOverview();
      testimonials();
    } else if (
      currentLocation.endsWith('/smartslides/examples') ||
      currentLocation.endsWith('/smartslides/examples/') ||
      currentLocation.endsWith('/smartgalleries/examples') ||
      currentLocation.endsWith('/smartgalleries/examples/')
    ) {
      scrollSlideDown();
    } else if (currentLocation.endsWith('/smartslides') || currentLocation.endsWith('/smartslides/')) {
      SmartSlidesOverview();
    } else if (currentLocation.includes('/tutorials-smartalbums')) {
      smartalbumsVideoTutorials();
    } else if (currentLocation.includes('/about')) {
      about();
    } else if (
      currentLocation.endsWith('/trial_download_sign_up_follow/windows') ||
      currentLocation.endsWith('/trial_download_sign_up_follow/windows/') ||
      currentLocation.endsWith('/trial_download_sign_up_follow/mac') ||
      currentLocation.endsWith('/trial_download_sign_up_follow/mac/')
    ) {
      smartalbumsTrialDownloadSignupFollow();
    }
    else if (location.pathname.includes('/blog')) {
      blog();
    }
    else if (
      currentLocation.endsWith('/smartalbums/download/mac') ||
      currentLocation.endsWith('/smartalbums/download/mac/') ||
      currentLocation.endsWith('/smartalbums/download/windows') ||
      currentLocation.endsWith('/smartalbums/download/windows/')
    ) {
      smartalbumsDownload();
    }
    else if (
      currentLocation.endsWith('/smartalbums/whatsnew') ||
      currentLocation.endsWith('/smartalbums/whatsnew/')
    ) {
      smartalbums2022();
      testimonials();
    } else if (
      location.pathname.endsWith('/smartgalleries')
      || location.pathname.endsWith('/smartgalleries/')
    ) {
      GalleriesOverview();
    }

    //SAMMPS Features page 1
    else if (currentLocation.includes('/sammps') && !currentLocation.includes('/sammps2')) {
      sammpsFeatures();
    }

    //SAMMPS Features page 2
    else if (currentLocation.includes('/sammps2')) {
      sammpsFeatures2();
    }

    //Set cookie for the Sentry clieent
    else if (currentLocation.startsWith('/service/set-sentry-cookie')) {
      setSentryCookie();
    }

    //Homepage
    else if (
      currentLocation.length === 0 || currentLocation === '' || currentLocation === '/' ||
      currentLocation === '/fr-fr/' || currentLocation === '/fr-fr' ||
      currentLocation === '/de-de/' || currentLocation === '/de-de' ||
      currentLocation === '/it-it/' || currentLocation === '/it-it' ||
      currentLocation === '/pt-pt/' || currentLocation === '/pt-pt' ||
      currentLocation === '/es-es/' || currentLocation === '/es-es'
    ) {
      HomePage();
      SmartSlidesOverview();
    }

    //Affiliates page
    else if (currentLocation.includes('/affiliates')) {
      AffiliatesPage();
    }

    //MKT Landing pages
    else if (
      currentLocation.includes('/landing') ||
      currentLocation.includes('smartalbums/aiassistant')
    ) {
      LandingPage();
    }

    //Calculator page
    else if (currentLocation.includes('/calculator')) {
      CalculatorPage();
    }

    //Lead Magnet pages
    if ($('#px-layout').hasClass('mkt-landing')) {
      LeadMagnet();
    }

    post();

    RudderStack();
    trackMarketingRoute();

    $('html').addClass('-processed');
  }

  $(document).ready(function () {
    pagesScripts();
  });
}
